import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../axiosInstance/axiosInstance";
import { BookableSlotsResponse } from "../contracts/BookableSlotsResponse";
import { BookableSlotWindowResponse } from "../contracts/BookableSlotWindowResponse";
import { ReplaceBookableSlotWindowsRequest } from "../contracts/ReplaceBookableSlotWindowsRequest";
import { SlotRequestRequest } from "../contracts/SlotRequestRequest";
import { SlotRequestResponse } from "../contracts/SlotRequestResponse";
import { mapResponseToBookableSlots } from "../mappers/BookableSlotsResponseMapper";
import { BookableSlots } from "../models/BookableSlots";
import { SlotApiError, toSlotError } from "../utils/slotApiError";

const WINDOWS_URL = "/instructors/me/bookable-slot-windows";

/** The signed-in instructor's bookable windows. */
export const fetchSlotWindows = createAsyncThunk<BookableSlotWindowResponse[], void, { rejectValue: SlotApiError }>(
  "bookableSlots/fetchWindows",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get<BookableSlotWindowResponse[]>(WINDOWS_URL);
      return response.data;
    } catch (error) {
      return rejectWithValue(toSlotError(error));
    }
  },
);

/** Replaces the instructor's whole set of windows. */
export const saveSlotWindows = createAsyncThunk<
  BookableSlotWindowResponse[],
  ReplaceBookableSlotWindowsRequest,
  { rejectValue: SlotApiError }
>("bookableSlots/saveWindows", async (request, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.put<BookableSlotWindowResponse[]>(WINDOWS_URL, request);
    return response.data;
  } catch (error) {
    return rejectWithValue(toSlotError(error));
  }
});

/** Staff: one instructor's free slots for a member (default: the member's assigned instructor). */
export const fetchMemberBookableSlots = createAsyncThunk<
  BookableSlots,
  { memberId: string; instructorId?: string | null },
  { rejectValue: SlotApiError }
>("bookableSlots/fetchMemberSlots", async ({ memberId, instructorId }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get<BookableSlotsResponse>(`/members/${memberId}/bookable-slots`, {
      params: instructorId ? { instructorId } : undefined,
    });
    return mapResponseToBookableSlots(response.data);
  } catch (error) {
    return rejectWithValue(toSlotError(error));
  }
});

/** Staff: books a member into a slot, Confirmed at once. */
export const bookSlotForMember = createAsyncThunk<
  SlotRequestResponse,
  { memberId: string; request: SlotRequestRequest },
  { rejectValue: SlotApiError }
>("bookableSlots/bookForMember", async ({ memberId, request }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<SlotRequestResponse>(`/members/${memberId}/slot-bookings`, request);
    return response.data;
  } catch (error) {
    return rejectWithValue(toSlotError(error));
  }
});
